// Tiny OTLP HTTP/JSON emitter. No OTel SDK — we hand-shape the JSON to keep
// the bundle small. Supports counters (Sum) and histograms.
//
// The backend's /v1/telemetry endpoint parses the standard OTLP
// `resourceMetrics` shape and maps it to prom-client counters/histograms.

type LabelValue = string | number | boolean;
export type Labels = Record<string, LabelValue>;

interface CounterPoint {
  kind: 'counter';
  name: string;
  value: number;
  labels: Labels;
  tNano: string;
}

interface HistogramPoint {
  kind: 'histogram';
  name: string;
  value: number;
  labels: Labels;
  tNano: string;
}

type Point = CounterPoint | HistogramPoint;

const buffer: Point[] = [];
const BUFFER_LIMIT = 200;
const FLUSH_MS = 5000;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;

function nowNano(): string {
  const ms = Date.now();
  return `${ms}000000`;
}

function toKeyValue(labels: Labels): Array<{ key: string; value: { stringValue: string } }> {
  return Object.entries(labels).map(([k, v]) => ({
    key: k,
    value: { stringValue: String(v) },
  }));
}

function buildOtlpPayload(points: Point[]) {
  const byName = new Map<string, { kind: Point['kind']; points: Point[] }>();
  for (const p of points) {
    const existing = byName.get(p.name);
    if (existing) existing.points.push(p);
    else byName.set(p.name, { kind: p.kind, points: [p] });
  }

  const metrics: unknown[] = [];
  for (const [name, { kind, points: pts }] of byName) {
    if (kind === 'counter') {
      metrics.push({
        name,
        sum: {
          isMonotonic: true,
          aggregationTemporality: 2, // CUMULATIVE
          dataPoints: pts.map((p) => ({
            attributes: toKeyValue(p.labels),
            timeUnixNano: p.tNano,
            asDouble: p.value,
          })),
        },
      });
    } else {
      // Histogram as a single-observation data point per event. Backend converts
      // to prom-client histogram observations.
      metrics.push({
        name,
        histogram: {
          aggregationTemporality: 2,
          dataPoints: pts.map((p) => ({
            attributes: toKeyValue(p.labels),
            timeUnixNano: p.tNano,
            count: '1',
            sum: p.value,
          })),
        },
      });
    }
  }

  return {
    resourceMetrics: [
      {
        resource: {
          attributes: toKeyValue({ 'service.name': 'singlings-frontend' }),
        },
        scopeMetrics: [
          {
            scope: { name: 'singlings.frontend' },
            metrics,
          },
        ],
      },
    ],
  };
}

async function flush() {
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, buffer.length);
  const payload = buildOtlpPayload(batch);
  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon('/v1/telemetry', blob);
      if (ok) return;
    }
    await fetch('/v1/telemetry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Drop silently. Telemetry loss is preferable to noisy console.
  }
}

export function initTelemetry() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  flushTimer = setInterval(flush, FLUSH_MS);
  window.addEventListener('pagehide', () => {
    void flush();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}

export function incCounter(name: string, value = 1, labels: Labels = {}) {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(value) || value < 0) return;
  buffer.push({ kind: 'counter', name, value, labels, tNano: nowNano() });
  if (buffer.length >= BUFFER_LIMIT) void flush();
}

export function observe(name: string, value: number, labels: Labels = {}) {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(value)) return;
  buffer.push({ kind: 'histogram', name, value, labels, tNano: nowNano() });
  if (buffer.length >= BUFFER_LIMIT) void flush();
}

export function shutdownTelemetry() {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = null;
  void flush();
}
